import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { ID } from '../../_kernel/ID'
import { Name } from '../../_kernel/Name'
import { Money } from '../../_kernel/Money'
import { Currency } from '../../_kernel/Currency'
import { Board } from './Board'
import { BoardCode } from './BoardCode'
import { Rate } from './Rate'
import { Room } from './Room'
import { Price } from '../../_kernel/Price'
import type { GetBookingRulesAvailabilityIdResponseType } from '../infrastructure/HTTPBookingRepository/schemas/v2/availability/:availabilityId/bookingRules'
import { BoardGroup } from './BoardGroup'
import { Pax } from '../../shared/domain/Pax'
import { Allotment } from './Allotment'
import { Market } from '../../shared/domain/Market'
import type { Currencies } from '../../_kernel/Currency'

export const AvailabilityValidation = z.object({
  id: z.instanceof(ID, { message: '[Availability] id is required' }),
  board: z.instanceof(Board, { message: '[Availability] board is required' }),
  rate: z.instanceof(Rate, { message: '[Availability] rate is required' }),
  room: z.instanceof(Room, { message: '[Availability] room is required' }),
  price: z.instanceof(Price, { message: '[Availability] price is required' }),
})

export class Availability extends Model {
  static create({ id, board, rate, room, price }: z.infer<typeof AvailabilityValidation>) {
    AvailabilityValidation.parse({ id, board, rate, room, price })
    return new Availability(id, board, rate, room, price)
  }

  static fromJSON(json: ReturnType<Availability['toJSON']>) {
    return new Availability(
      ID.create({ value: json.id }),
      Board.fromJSON(json.board),
      Rate.fromJSON(json.rate),
      Room.fromJSON(json.room),
      Price.fromJSON(json.price)
    )
  }

  static fromBookingRulesAPI({
    availabilityId,
    json,
  }: {
    availabilityId: string
    json: GetBookingRulesAvailabilityIdResponseType
  }) {
    // Create Board domain object from flat API fields
    const board = Board.create({
      code: BoardCode.fromLiteral(json.board_code),
      name: Name.create({ value: json.board_name }),
      group: BoardGroup.create({
        code: json.board_code,
        name: json.board_name,
      }),
    })

    // Create Rate domain object from flat API fields
    const rate = Rate.fromLiterals({
      paymentPolicy: json.rate_class,
      cancellationPolicyFromDate: json.cancellation_policies[0]?.from,
      cancellationPolicyAmount: json.cancellation_policies[0]?.amount,
      currency: json.currency as Currencies,
    })

    // Create Room domain object from flat API fields
    const room = Room.create({
      id: ID.create({ value: availabilityId }),
      name: Name.create({ value: json.room_name }),
      pax: Pax.fromAvailabilityApi({
        adults: json.adults,
        children: json.children_ages,
      }),
      allotment: Allotment.create({ value: json.allotment }),
    })

    // Create Price domain object from flat API fields
    const currency = Currency.create({ value: json.currency as Currencies })
    const market = Market.fromBookingRulesAPI({ market: json.market })

    const price = Price.create({
      total: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      totalWithoutDiscount: Money.create({
        amount: json.bighead_rate,
        currency,
      }),
      totalWithFees: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      perNight: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      perNightWithFees: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      perNightSubtotal: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      subtotal: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      subtotalWithFees: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      subtotalNightlyWithFees: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      market,
    })

    return new Availability(ID.create({ value: availabilityId }), board, rate, room, price)
  }

  constructor(
    private readonly _id: ID,
    private readonly _board: Board,
    private readonly _rate: Rate,
    private readonly _room: Room,
    private readonly _price: Price
  ) {
    super()
  }

  id() {
    return this._id.value
  }

  roomID() {
    return this._room.roomID()
  }

  room() {
    return this._room
  }

  board() {
    return this._board
  }

  boardName() {
    return this._board.name() ?? ''
  }

  boardCode() {
    return this._board.code() ?? ''
  }

  rate() {
    return this._rate
  }

  paymentPolicy() {
    return this._rate.paymentPolicy()
  }

  cancellationPolicyFromDate() {
    return this._rate.cancellationPolicyFromDate()
  }

  cancellationPolicyAmount() {
    return this._rate.cancellationPolicyAmount()
  }

  price() {
    return this._price
  }

  priceTotalAmount() {
    return this._price.totalAmount()
  }

  currency() {
    return this._price.currency()
  }

  priceDiff(priceBefore: Price) {
    return this._price.diff(priceBefore)
  }

  toJSON() {
    return {
      id: this._id.value,
      board: this._board.toJSON(),
      rate: this._rate.toJSON(),
      room: this._room.toJSON(),
      price: this._price.toJSON(),
    }
  }
}
